<script setup lang="ts">
import { useComputedAttrs, useComputedInput, useEventsToValidate } from './helpers'
import { RadioGroupInputProps } from './types'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps<RadioGroupInputProps>()
const emit = defineEmits(['update:modelValue', 'change', 'blur', 'validate', 'invalid'])
const input = useComputedInput(props, emit)
const attrs = useComputedAttrs(props, 'radio-group-vertical')
const events = useEventsToValidate(emit)
</script>

<template>
  <div
    v-for="option in props.options"
    :key="`${attrs.id}-${option.label}`"
    class="radio-group-item"
    :class="{ selected: option.value === input }"
    @click="input = option.value"
  >
    <input
      class="radio-input"
      v-model="input"
      v-autofocus="props.autofocus"
      v-bind="attrs"
      :id="`${attrs.id}-${option.label}`"
      :value="option.value"
      :checked="option.value === input"
      type="radio"
      :aria-required="props.required"
      :aria-invalid="props.invalid"
      v-on="events"
    />
    <label class="label" :for="`${attrs.id}-${option.label}`">
      {{ option.label }}
    </label>
    <span v-if="option.description">{{ option.description }}</span>
  </div>
</template>

<style scoped>
.radio-group-item {
  display: flex;
  align-items: center;
  padding: 16px;
  margin: 0;
  color: var(--c-radio-group-vertical-inactive-color);
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;

  &:not(:last-of-type) {
    border-bottom: 1px solid var(--c-radio-group-vertical-separator);
  }

  &:hover {
    background-color: var(--c-radio-group-vertical-hover-bg);
  }

  &.selected {
    background-color: var(--c-radio-group-vertical-selected-bg);
    color: var(--c-radio-group-vertical-selected-color);
  }

  &.selected:hover {
    background-color: var(--c-radio-group-vertical-selected-bg);
  }

  label {
    margin: 0 16px 0 0;
    font-family: var(--font-family-body);
    font-size: var(--font-size-body);
    font-weight: var(--font-weight-regular);
    line-height: var(--line-height-body);
    color: var(--c-input-text);
    cursor: pointer;
  }
}
</style>
